import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import CustomModal from '../../../../components/CustomModal/CustomModal';
import Button from '../../../../components/Button/Button';
import NotificationSnackbar from '../../../../components/NotificationSnackbar/NotificationSnackbar';
import { cn } from '../../../../lib/utils';
import BotBuilderService from '../../../../services/BotBuilderService/BotBuilderService';
import BulkSimpleIntentPayload from '../../../../types/BotBuilder/BulkSimpleIntentPayload';
import BulkImportRowError from '../../../../types/BotBuilder/BulkImportRowError';
import { hasErrors, parseFaqCsv, validateRows } from './faq-import';
import ParsedRow from './ParsedRow';
import RowIssue from './RowIssue';

interface ImportFaqModalProps {
  isOpen: boolean;
  chatbotId: number;
  onClose: () => void;
  onSuccess: () => void;
}

type Stage = 'idle' | 'parsing' | 'previewing' | 'submitting';

const TEMPLATE_URL = `${process.env.PUBLIC_URL ?? ''}/faq-import-template.csv`;

function serverIssuesForRow(
  rowIndex: number,
  errors: BulkImportRowError[]
): RowIssue[] {
  return errors
    .filter((e) => e.rowIndex === rowIndex)
    .map((e) => ({ code: 'server-error' as const, message: e.message }));
}

function mergeServerIssues(
  rows: ParsedRow[],
  errors: BulkImportRowError[]
): ParsedRow[] {
  return rows.map((row, index) => {
    const serverIssues = serverIssuesForRow(index, errors);
    if (serverIssues.length === 0) return row;
    return { ...row, issues: [...row.issues, ...serverIssues] };
  });
}

function ImportFaqModal({
  isOpen,
  chatbotId,
  onClose,
  onSuccess
}: Readonly<ImportFaqModalProps>): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>('idle');
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const reset = useCallback((): void => {
    setStage('idle');
    setFileName(null);
    setRows([]);
    setHeaderError(null);
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback((): void => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStage('parsing');
      setFileName(file.name);
      setHeaderError(null);
      setSubmitError(null);
      setRows([]);

      try {
        const result = await parseFaqCsv(file);
        if (result.kind === 'header-error') {
          setHeaderError(result.message);
          setStage('idle');
          return;
        }
        setRows(validateRows(result.rows));
        setStage('previewing');
      } catch (err) {
        setHeaderError(
          err instanceof Error
            ? `Could not read file: ${err.message}`
            : 'Could not read file.'
        );
        setStage('idle');
      }
    },
    []
  );

  const summary = useMemo(() => {
    const total = rows.length;
    const invalid = rows.filter((r) => r.issues.length > 0).length;
    return { total, invalid, valid: total - invalid };
  }, [rows]);

  const submitDisabled =
    stage !== 'previewing' || rows.length === 0 || hasErrors(rows);

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (submitDisabled) return;
    setStage('submitting');
    setSubmitError(null);

    const payload: BulkSimpleIntentPayload[] = rows.map((r) => ({
      name: r.name,
      utterances: r.triggers,
      responseBlocks: r.responseBlocks,
      topic: r.topic ? r.topic : null
    }));

    try {
      const result = await BotBuilderService.bulkCreateIntents(
        chatbotId,
        payload
      );

      if (result.errors.length > 0) {
        setRows((prev) => mergeServerIssues(prev, result.errors));
        setSubmitError(
          `Import rejected: ${result.errors.length} row${result.errors.length === 1 ? '' : 's'} need fixing.`
        );
        setStage('previewing');
        return;
      }

      setSuccessMessage(
        `Imported ${result.created} FAQ${result.created === 1 ? '' : 's'} successfully`
      );
      setShowSuccess(true);
      onSuccess();
      handleClose();
    } catch (err: any) {
      setSubmitError(
        err?.apiError?.message ??
          'Could not import FAQs. Please try again later.'
      );
      setStage('previewing');
    }
  }, [submitDisabled, rows, chatbotId, onSuccess, handleClose]);

  return (
    <>
      <CustomModal
        open={isOpen}
        onClose={handleClose}
        title='Import FAQs from CSV'
        description='Upload a CSV to add multiple FAQs at once. Errors are shown in a preview before anything is saved.'
        maxWidth='5xl'
      >
        <div className='flex max-h-[75vh] flex-col gap-4'>
          <div className='rounded-md border border-gray-200 bg-gray-50 p-4'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <h4 className='text-sm font-semibold text-gray-900'>
                  File format
                </h4>
                <p className='mt-0.5 text-xs text-gray-500'>
                  First row must contain headers (case-insensitive).
                </p>
              </div>
              <a
                href={TEMPLATE_URL}
                download='faq-import-template.csv'
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-md',
                  'border border-gray-300 bg-white px-3 py-1.5',
                  'text-xs font-medium text-gray-700 hover:bg-gray-100'
                )}
              >
                <Download size={14} />
                Download template
              </a>
            </div>

            <dl className='mt-3 grid grid-cols-[auto_auto_1fr] gap-x-3 gap-y-2 text-sm'>
              <dt className='font-mono text-gray-900'>name</dt>
              <dd className='text-xs font-medium uppercase tracking-wide text-rose-600'>
                required
              </dd>
              <dd className='text-gray-600'>
                Unique question name, max 255 characters.
              </dd>

              <dt className='font-mono text-gray-900'>triggers</dt>
              <dd className='text-xs font-medium uppercase tracking-wide text-rose-600'>
                required
              </dd>
              <dd className='text-gray-600'>
                Phrases that fire this FAQ, pipe-separated. Example:{' '}
                <span className='font-mono text-gray-700'>
                  When are you open|What time do you open
                </span>
              </dd>

              <dt className='font-mono text-gray-900'>response</dt>
              <dd className='text-xs font-medium uppercase tracking-wide text-rose-600'>
                required
              </dd>
              <dd className='text-gray-600'>
                Markdown text. Newlines inside the cell (Alt+Enter in Excel)
                split into separate message blocks.
              </dd>

              <dt className='font-mono text-gray-900'>topic</dt>
              <dd className='text-xs font-medium uppercase tracking-wide text-gray-400'>
                optional
              </dd>
              <dd className='text-gray-600'>
                Free text — matches an existing topic or creates a new one.
              </dd>
            </dl>
          </div>

          <div className='flex items-center gap-3'>
            <input
              ref={fileInputRef}
              type='file'
              accept='.csv,text/csv'
              onChange={handleFileChange}
              disabled={stage === 'parsing' || stage === 'submitting'}
              className={cn(
                'block w-full text-sm text-gray-700',
                'file:mr-3 file:rounded file:border-0',
                'file:bg-primary file:px-4 file:py-2',
                'file:text-sm file:font-medium file:text-white',
                'hover:file:bg-primary/90 disabled:opacity-60'
              )}
            />
            {fileName && stage !== 'idle' && (
              <span className='whitespace-nowrap text-xs text-gray-500'>
                {fileName}
              </span>
            )}
          </div>

          {stage === 'parsing' && (
            <p className='text-sm text-gray-500'>Parsing file...</p>
          )}

          {headerError && (
            <div className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
              {headerError}
            </div>
          )}

          {(stage === 'previewing' || stage === 'submitting') &&
            rows.length > 0 && (
              <>
                <div className='flex items-center justify-between text-sm'>
                  <span className='text-gray-700'>
                    <span className='font-medium'>{summary.total}</span> rows —{' '}
                    <span className='text-emerald-700'>
                      {summary.valid} valid
                    </span>
                    {summary.invalid > 0 && (
                      <>
                        ,{' '}
                        <span className='text-red-700'>
                          {summary.invalid} with issues
                        </span>
                      </>
                    )}
                  </span>
                </div>

                {summary.invalid > 0 && (
                  <div className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
                    Fix the highlighted rows in your file and re-upload to
                    continue.
                  </div>
                )}

                <div className='min-h-0 flex-1 overflow-auto rounded border border-gray-200'>
                  <table className='w-full table-fixed text-sm'>
                    <colgroup>
                      <col className='w-12' />
                      <col className='w-48' />
                      <col className='w-16' />
                      <col className='w-28' />
                      <col />
                      {summary.invalid > 0 && <col className='w-64' />}
                    </colgroup>
                    <thead className='sticky top-0 bg-gray-50'>
                      <tr className='border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500'>
                        <th className='px-3 py-2'>Line</th>
                        <th className='px-3 py-2'>Name</th>
                        <th className='px-3 py-2'>Triggers</th>
                        <th className='px-3 py-2'>Topic</th>
                        <th className='px-3 py-2'>Response</th>
                        {summary.invalid > 0 && (
                          <th className='px-3 py-2'>Issues</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        const invalid = row.issues.length > 0;
                        const responsePreview = row.responseBlocks[0] ?? '';
                        return (
                          <tr
                            key={`${row.lineNumber}-${idx}`}
                            className={cn(
                              'border-b border-gray-100 align-top last:border-0',
                              invalid && 'bg-red-50'
                            )}
                          >
                            <td className='px-3 py-2 text-xs text-gray-500'>
                              {row.lineNumber}
                            </td>
                            <td className='break-words px-3 py-2 text-gray-900'>
                              {row.name || (
                                <span className='text-red-600'>—</span>
                              )}
                            </td>
                            <td className='px-3 py-2 text-xs text-gray-600'>
                              {row.triggers.length || (
                                <span className='text-red-600'>0</span>
                              )}
                            </td>
                            <td className='truncate px-3 py-2 text-gray-600'>
                              {row.topic || (
                                <span className='text-gray-400'>—</span>
                              )}
                            </td>
                            <td className='truncate px-3 py-2 text-gray-600'>
                              {responsePreview ? (
                                <>
                                  {row.responseBlocks.length > 1 && (
                                    <span className='mr-1 inline-flex rounded-full bg-gray-200 px-1.5 text-xs text-gray-700'>
                                      {row.responseBlocks.length} blocks
                                    </span>
                                  )}
                                  {responsePreview}
                                </>
                              ) : (
                                <span className='text-red-600'>—</span>
                              )}
                            </td>
                            {summary.invalid > 0 && (
                              <td className='break-words px-3 py-2 text-xs text-red-700'>
                                {row.issues.map((i) => i.message).join('; ')}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          {stage === 'previewing' && rows.length === 0 && !headerError && (
            <div className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
              The file did not contain any data rows.
            </div>
          )}

          {submitError && (
            <div className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
              {submitError}
            </div>
          )}

          <div className='flex items-center justify-end gap-2 pt-2'>
            <Button
              type='button'
              variant='outline'
              onClick={handleClose}
              disabled={stage === 'submitting'}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={handleConfirm}
              loading={stage === 'submitting'}
              loadingText='Importing...'
              disabled={submitDisabled}
              icon={<Upload size={16} />}
            >
              {rows.length > 0 && !hasErrors(rows)
                ? `Import ${rows.length}`
                : 'Import'}
            </Button>
          </div>
        </div>
      </CustomModal>

      <NotificationSnackbar
        message={successMessage}
        severity='success'
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
}

export default ImportFaqModal;
